import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setValid(false); setValidating(false); return; }
    axios.get(`${API}/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(r => {
        setValid(r.data.valid);
        setReason(r.data.reason || '');
        setEmail(r.data.email || '');
      })
      .catch(() => setValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (pwd !== confirm) { setError('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <Card className="w-full max-w-md border-zinc-200" data-testid="reset-password-page">
        <CardHeader>
          <CardTitle className="text-xl">Set a new password</CardTitle>
        </CardHeader>
        <CardContent>
          {!valid ? (
            <div className="text-center py-4" data-testid="reset-invalid">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h3 className="font-semibold text-zinc-900 mb-1">This link is no longer valid</h3>
              <p className="text-sm text-zinc-600 mb-5">
                {reason === 'expired' ? 'It has expired (links are valid for 1 hour).' :
                 reason === 'already_used' ? 'It has already been used to reset a password.' :
                 'The link could not be found.'} Request a fresh one below.
              </p>
              <Link to="/forgot-password"><Button className="rounded-md bg-brand-primary text-white">Request a new link</Button></Link>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="font-semibold text-zinc-900 mb-1">Password updated</h3>
              <p className="text-sm text-zinc-600">Redirecting to login…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-zinc-600">
                Resetting password for <span className="font-mono text-zinc-900">{email}</span>
              </p>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700">New password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input type="password" minLength={8} required value={pwd} onChange={e => setPwd(e.target.value)} placeholder="At least 8 characters" className="rounded-md pl-9" data-testid="reset-pwd" autoFocus />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700">Confirm password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)} className="rounded-md pl-9" data-testid="reset-confirm" />
                </div>
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="reset-error">{error}</div>}
              <Button type="submit" disabled={submitting} className="w-full rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white" data-testid="reset-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
